import { notification } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ConfigurationItem,
  ConfigurationType,
  useAddConfigurationFieldMutation,
  useUpdateConfigurationFieldMutation,
  useUploadConfigurationColorMutation,
  useUploadConfigurationImageMutation,
} from "shared/api";
import { handleRequestError } from "shared/helpers";
import { ConfigurationForm } from "shared/ui";

export interface UseConfigurationsProps {
  uploadedFile: File | null;
  closeModalAndClearUploadedImage: () => void;
  values: ConfigurationForm;
  companyNameConfiguration?: ConfigurationItem;
  timezoneConfiguration?: ConfigurationItem;
  color: string;
  faviconLightFile?: File | null;
  faviconDarkFile?: File | null;
  onAfterSave?: () => void;
}

export const useConfigurations = ({
  uploadedFile,
  closeModalAndClearUploadedImage,
  values,
  companyNameConfiguration,
  timezoneConfiguration,
  color,
  faviconLightFile,
  faviconDarkFile,
  onAfterSave,
}: UseConfigurationsProps) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();

  const [uploadConfigurationImage] = useUploadConfigurationImageMutation();
  const [addConfigurationField] = useAddConfigurationFieldMutation();
  const [updateConfigurationField] = useUpdateConfigurationFieldMutation();
  const [uploadConfigurationColor] = useUploadConfigurationColorMutation();

  const handleCustomerImageLogo = async () => {
    try {
      if (uploadedFile) {
        const formData = new FormData();
        formData.append("file", uploadedFile);
        await uploadConfigurationImage({ image: formData });
      }

      api.success({
        message: t("sidebar.logoSuccessUpload"),
      });
    } catch (error) {
      handleRequestError(error, t("sidebar.error"));

      api.error({
        message: t("sidebar.error"),
      });
    } finally {
      closeModalAndClearUploadedImage();
    }
  };

  const handleAddCompanyName = async () => {
    try {
      await addConfigurationField({
        key: ConfigurationType.CompanyName,
        value: values?.companyName!,
        file: null,
      }).unwrap();

      api.success({
        message: t("users-page.success-title"),
      });
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const handleUpdateCompanyName = async () => {
    try {
      await updateConfigurationField({
        key: ConfigurationType.CompanyName,
        value: values?.companyName!,
        file: null,
      }).unwrap();

      api.success({
        message: t("users-page.success-title"),
      });
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const handleAddTimezone = async () => {
    try {
      await addConfigurationField({
        key: ConfigurationType.Timezone,
        value: values?.timezone!,
        file: null,
      }).unwrap();

      api.success({
        message: t("users-page.success-title"),
      });
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const handleUpdateTimezone = async () => {
    try {
      await updateConfigurationField({
        key: ConfigurationType.Timezone,
        value: values?.timezone!,
        file: null,
      }).unwrap();

      api.success({
        message: t("users-page.success-title"),
      });
    } catch (error) {
      api.error({
        message: t("sidebar.error"),
      });
    }
  };

  const handleColorChange = async (color: string) => {
    try {
      await uploadConfigurationColor({
        key: "color",
        value: color,
        file: null,
      }).unwrap();

      localStorage.setItem("color-theme", JSON.stringify(color));

      api.success({
        message: t("sidebar.colorSuccessUpload", { color }),
      });
    } catch (error) {
      handleRequestError(error, t("sidebar.error"));
      api.error({ message: t("sidebar.error") });
    }
  };

  const handleFaviconUpload = async (
    file: File | null | undefined,
    key: string
  ) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("key", key);
      await uploadConfigurationImage({ image: formData });
    } catch (error) {
      api.error({ message: t("sidebar.error") });
    }
  };

  const handleConfigurationsRequests = async () => {
    await handleCustomerImageLogo();

    if (timezoneConfiguration) {
      await handleUpdateTimezone();
    } else {
      await handleAddTimezone();
    }

    if (companyNameConfiguration) {
      await handleUpdateCompanyName();
    } else {
      await handleAddCompanyName();
    }

    if (values?.color && values.color !== color) {
      await handleColorChange(values.color);
    }

    await handleFaviconUpload(faviconLightFile, "favicon-light");
    await handleFaviconUpload(faviconDarkFile, "favicon-dark");

    onAfterSave?.();
    window.location.reload();
  };

  useEffect(() => {
    if (!localStorage.getItem("color-theme")) {
      handleColorChange("#002F5F");
    }
  }, []);

  return { handleConfigurationsRequests, contextHolder, handleColorChange };
};
